/**
 * Session Report Service
 * 
 * Handles the new structured session report format
 * Provides methods for creating, updating, and retrieving session reports
 */

// Set the base URL for your API
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';

class SessionReportService {
    /**
     * Get session report (with auto-migration for old format)
     */
    async getSessionReport(sessionId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/report`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                const error = new Error(errorData.message || 'Failed to get session report');
                error.status = response.status;
                throw error;
            }

            const result = await response.json();
            
            // Validate the response structure
            if (!result || typeof result !== 'object') {
                throw new Error('Invalid response format from server');
            }
            
            return result;
        } catch (error) {
            // Don't log 404 errors - they're expected for friend posts or missing sessions
            if (error.status !== 404) {
                console.error('Error getting session report:', error);
            }
            throw error;
        }
    }

    /**
     * Update session report
     */
    async updateSessionReport(sessionId, reportData, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/report`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(reportData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update session report');
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error updating session report:', error);
            throw error;
        }
    }

    /**
     * Add timeline entry to session report
     */
    async addTimelineEntry(sessionId, timelineEntry, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/timeline`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(timelineEntry)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add timeline entry');
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error adding timeline entry:', error);
            throw error;
        }
    }

    /**
     * Add highlight to session report
     */
    async addHighlight(sessionId, highlightData, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/highlights`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(highlightData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add highlight');
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error adding highlight:', error);
            throw error;
        }
    }

    /**
     * Update final outcome
     */
    async updateFinalOutcome(sessionId, outcomeData, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/outcome`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(outcomeData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update final outcome');
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error updating final outcome:', error);
            throw error;
        }
    }

    /**
     * Create a new timeline entry from session data
     */
    createTimelineEntry(phase, stepIndex = null, stepTitle = null, notes = '', status = 'ok', trigger = 'user_action') {
        return {
            phase,
            step_index: stepIndex,
            step_title: stepTitle,
            notes,
            photos: [],
            status,
            trigger,
            duration_seconds: null
        };
    }

    /**
     * Create a new highlight
     */
    createHighlight(type, title, description, stepIndex = null, userRating = null, tags = []) {
        return {
            type,
            title,
            description,
            photos: [],
            step_index: stepIndex,
            user_rating: userRating,
            tags
        };
    }

    /**
     * Create final outcome data
     */
    createFinalOutcome(rating, comments, successIndicators = [], issuesEncountered = [], whatWorkedWell = [], whatToImprove = [], nextTimeNotes = '') {
        return {
            overall_rating: rating,
            comments,
            success_indicators: successIndicators,
            issues_encountered: issuesEncountered,
            what_worked_well: whatWorkedWell,
            what_to_improve: whatToImprove,
            next_time_notes: nextTimeNotes
        };
    }

    /**
     * Format timeline entry for display
     */
    formatTimelineEntry(entry) {
        return {
            id: entry.id,
            timestamp: new Date(entry.timestamp),
            phase: entry.phase,
            stepIndex: entry.step_index,
            stepTitle: entry.step_title,
            notes: entry.notes,
            photos: entry.photos || [],
            status: entry.status,
            trigger: entry.trigger,
            duration: entry.duration_seconds,
            chefNotes: entry.chef_notes,
            chefSuggestions: entry.chef_suggestions || []
        };
    }

    /**
     * Format highlight for display
     */
    formatHighlight(highlight) {
        return {
            id: highlight.id,
            timestamp: new Date(highlight.timestamp),
            type: highlight.type,
            title: highlight.title,
            description: highlight.description,
            photos: highlight.photos || [],
            stepIndex: highlight.step_index,
            userRating: highlight.user_rating,
            tags: highlight.tags || []
        };
    }

    /**
     * Format final outcome for display
     */
    formatFinalOutcome(outcome) {
        return {
            dishPhoto: outcome.dish_photo,
            overallRating: outcome.overall_rating,
            comments: outcome.comments,
            successIndicators: outcome.success_indicators || [],
            issuesEncountered: outcome.issues_encountered || [],
            whatWorkedWell: outcome.what_worked_well || [],
            whatToImprove: outcome.what_to_improve || [],
            nextTimeNotes: outcome.next_time_notes || ''
        };
    }

    /**
     * Get phase display name
     */
    getPhaseDisplayName(phase) {
        const phaseNames = {
            'preparation': 'Preparation',
            'cooking': 'Cooking',
            'resting': 'Resting',
            'serving': 'Serving',
            'cleanup': 'Cleanup',
            'paused': 'Paused',
            'completed': 'Completed'
        };
        return phaseNames[phase] || phase;
    }

    /**
     * Get status display name and color
     */
    getStatusDisplay(status) {
        const statusConfig = {
            'ok': { name: 'Good', color: 'green' },
            'delayed': { name: 'Delayed', color: 'yellow' },
            'needs_attention': { name: 'Needs Attention', color: 'red' },
            'completed': { name: 'Completed', color: 'green' },
            'skipped': { name: 'Skipped', color: 'gray' },
            'modified': { name: 'Modified', color: 'blue' }
        };
        return statusConfig[status] || { name: status, color: 'gray' };
    }

    /**
     * Get highlight type display name and icon
     */
    getHighlightTypeDisplay(type) {
        const typeConfig = {
            'success': { name: 'Success', icon: '✓' },
            'issue': { name: 'Issue', icon: '⚠️' },
            'experiment': { name: 'Experiment', icon: '🧪' },
            'discovery': { name: 'Discovery', icon: '💡' },
            'milestone': { name: 'Milestone', icon: '🎯' },
            'tip_learned': { name: 'Tip Learned', icon: '💡' }
        };
        return typeConfig[type] || { name: type, icon: '📝' };
    }
}

// Create singleton instance
const sessionReportService = new SessionReportService();

export default sessionReportService;
